import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Chip, CircularProgress, Collapse, IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { fieldSx, labelSx } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

const ENTIDADES = [
  { value: '',              label: 'Todas las entidades' },
  { value: 'medico',        label: 'Médicos' },
  { value: 'especialidad',  label: 'Especialidades' },
  { value: 'disponibilidad', label: 'Disponibilidad' },
  { value: 'disponibilidad_extraordinaria', label: 'Disponibilidad extraordinaria' },
  { value: 'ausencia',      label: 'Ausencias / licencias' },
  { value: 'paciente',      label: 'Pacientes' },
  { value: 'turno',         label: 'Turnos' },
  { value: 'evolucion',     label: 'Evoluciones (informes médicos)' },
];

const ACCIONES = [
  { value: '',        label: 'Todas las acciones' },
  { value: 'alta',     label: 'Alta' },
  { value: 'edicion',  label: 'Edición' },
  { value: 'baja',     label: 'Baja' },
];

const COLORES_ACCION = {
  alta: '#66bb6a',
  edicion: '#42a5f5',
  baja: '#ef5350',
};

function AccionChip({ valor }) {
  if (!valor) return <Typography sx={{ color: '#546e7a', fontSize: 12 }}>—</Typography>;
  return (
    <Chip
      label={valor}
      size="small"
      sx={{
        bgcolor: `${COLORES_ACCION[valor] || '#90a4ae'}22`,
        color: COLORES_ACCION[valor] || '#90a4ae',
        border: `1px solid ${COLORES_ACCION[valor] || '#90a4ae'}55`,
        fontSize: 11,
        height: 22,
        textTransform: 'capitalize',
      }}
    />
  );
}

function formatFechaHora(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function resumenDetalle(row) {
  const d = row.detalle;
  if (!d) return '—';
  if (row.entidad_tipo === 'turno' && row.accion === 'alta') {
    return `Turno creado: ${d.fecha || ''} ${(d.hora_inicio || '').substring(0, 5)} — ${d.paciente_nombre || 'sin paciente'}${d.especial ? ' (especial)' : ''}`.trim();
  }
  if (row.entidad_tipo === 'turno') {
    const estado = `Estado: ${d.antes?.estado || '—'} → ${d.despues?.estado || '—'}`;
    return d.motivo ? `${estado} — ${d.motivo}` : estado;
  }
  if (row.entidad_tipo === 'disponibilidad_extraordinaria' || row.entidad_tipo === 'ausencia') {
    const rango = row.entidad_tipo === 'ausencia' ? `${d.fecha_inicio || ''} a ${d.fecha_fin || ''}` : (d.fecha || '');
    const afectados = Array.isArray(d.turnos_afectados) ? d.turnos_afectados.length : (d.turnos_afectados || 0);
    const partes = [`${row.accion === 'alta' ? 'Alta' : 'Baja'}: ${rango}`.trim()];
    if (d.medico_nombre) partes.push(d.medico_nombre);
    if (row.accion === 'alta' && d.cancelar_turnos && afectados > 0) partes.push(`${afectados} turno${afectados !== 1 ? 's' : ''} cancelado${afectados !== 1 ? 's' : ''}`);
    return partes.join(' — ');
  }
  if (row.entidad_tipo === 'evolucion' && row.accion === 'baja') return 'Anulada';
  if (row.accion === 'alta') return d.nombre ? `Alta: ${d.nombre}` : 'Alta';
  if (row.accion === 'baja') return d.nombre ? `Baja: ${d.nombre}` : 'Baja';
  if (row.entidad_tipo === 'paciente' && d.campo === 'dni') {
    return `Corrección de DNI: ${d.antes || '—'} → ${d.despues || '—'} — ${d.motivo || ''}`.trim();
  }
  if (row.accion === 'edicion') {
    if (row.entidad_tipo === 'disponibilidad') return 'Modificó los horarios de atención';
    if (d.antes && d.despues) {
      const campos = Object.keys(d.despues).filter(k => JSON.stringify(d.antes[k]) !== JSON.stringify(d.despues[k]));
      return campos.length ? `Cambió: ${campos.map(c => CAMPO_LABEL[c] || c).join(', ')}` : 'Sin cambios de valor';
    }
  }
  return 'Ver detalle';
}

// Muestra antes/después campo por campo cuando el detalle tiene esa forma (el
// caso más común: médico, paciente, evolución, turno editados); si no encaja en
// ese patrón (alta/baja, corrección de DNI, etc.) cae a un JSON legible como respaldo.
function DetalleJSON({ detalle }) {
  if (!detalle) return null;
  if (Array.isArray(detalle.turnos_afectados) && detalle.turnos_afectados.length > 0) {
    return (
      <Box sx={{ bgcolor: '#0d1b2a', border: '1px solid #1e3a5f', borderRadius: 1, p: 1.5 }}>
        {detalle.motivo && (
          <Typography sx={{ fontSize: 12, color: '#90a4ae', mb: 1 }}>Motivo: {detalle.motivo}</Typography>
        )}
        <Typography sx={{ fontSize: 12, color: '#90a4ae', mb: 1 }}>
          Turnos cancelados por esta baja de disponibilidad:
        </Typography>
        <Table size="small">
          <TableBody>
            {detalle.turnos_afectados.map((t, i) => (
              <TableRow key={i} sx={{ '& td': { border: 'none', py: 0.4, fontSize: 12 } }}>
                <TableCell sx={{ color: '#e0e0e0', pl: 0 }}>{t.paciente_nombre || '—'}</TableCell>
                <TableCell sx={{ color: '#90a4ae' }}>DNI {t.paciente_dni || '—'}</TableCell>
                <TableCell sx={{ color: '#90a4ae' }}>{t.fecha || ''} {t.hora || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }
  const antes = detalle.antes, despues = detalle.despues;
  if (antes && despues && typeof antes === 'object' && typeof despues === 'object' && !Array.isArray(antes)) {
    const campos = Array.from(new Set([...Object.keys(antes), ...Object.keys(despues)]))
      .filter(k => JSON.stringify(antes[k]) !== JSON.stringify(despues[k]));
    return (
      <Box sx={{ bgcolor: '#0d1b2a', border: '1px solid #1e3a5f', borderRadius: 1, p: 1.5 }}>
        {detalle.motivo && (
          <Typography sx={{ fontSize: 12, color: '#90a4ae', mb: 1 }}>Motivo: {detalle.motivo}</Typography>
        )}
        {campos.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#546e7a' }}>Sin cambios de valor.</Typography>
        ) : (
          <Table size="small">
            <TableBody>
              {campos.map(campo => (
                <TableRow key={campo} sx={{ '& td': { border: 'none', py: 0.4, fontSize: 12 } }}>
                  <TableCell sx={{ color: '#90a4ae', width: 180, pl: 0 }}>{CAMPO_LABEL[campo] || campo}</TableCell>
                  <TableCell sx={{ color: '#ef9a9a' }}>{formatValor(antes[campo])}</TableCell>
                  <TableCell sx={{ color: '#546e7a', width: 20 }}>→</TableCell>
                  <TableCell sx={{ color: '#a5d6a7' }}>{formatValor(despues[campo])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  }
  return (
    <Box component="pre" sx={{
      m: 0, p: 1.5, bgcolor: '#0d1b2a', border: '1px solid #1e3a5f', borderRadius: 1,
      fontSize: 11, color: '#90a4ae', overflowX: 'auto', fontFamily: 'monospace',
    }}>
      {JSON.stringify(detalle, null, 2)}
    </Box>
  );
}

const ENTIDAD_LABEL = {
  medico: 'Médico',
  especialidad: 'Especialidad',
  disponibilidad: 'Disponibilidad',
  disponibilidad_extraordinaria: 'Disponibilidad extraordinaria',
  ausencia: 'Ausencia',
  paciente: 'Paciente',
  turno: 'Turno',
  evolucion: 'Evolución',
};

const CAMPO_LABEL = {
  nombre: 'Nombre', apellido: 'Apellido', telefono: 'Teléfono', email: 'Email',
  fecha_nacimiento: 'Fecha de nacimiento', estado: 'Estado',
  texto: 'Texto de la evolución', diagnostico: 'Diagnóstico',
  medicamentos: 'Medicamentos', estudios: 'Estudios', palabras_clave: 'Palabras clave',
};

function formatValor(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map(x => (typeof x === 'object' ? (x.nombre || JSON.stringify(x)) : x)).join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const hoy = new Date().toISOString().slice(0, 10);
const hace30 = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

export default function AuditoriaMedicos() {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [expandido, setExpandido] = useState(null);
  const [filtros, setFiltros] = useState({
    desde: hace30,
    hasta: hoy,
    usuario: '',
    entidad_tipo: '',
    accion: '',
  });

  const f = field => e => setFiltros(prev => ({ ...prev, [field]: e.target.value }));

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const params = {};
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      if (filtros.usuario.trim()) params.usuario = filtros.usuario.trim();
      if (filtros.entidad_tipo) params.entidad_tipo = filtros.entidad_tipo;
      if (filtros.accion) params.accion = filtros.accion;
      const res = await axios.get(`${API}/api/auditoria/clinica`, { params });
      setFilas(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar la auditoría.');
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box sx={{ p: 3, maxWidth: 1400 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 3 }}>
        Auditoría de Médicos y Agenda
      </Typography>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'flex-end' }}>
        <Box>
          <Typography sx={labelSx}>Desde</Typography>
          <TextField type="date" value={filtros.desde} onChange={f('desde')}
            inputProps={{ style: { colorScheme: 'dark' } }} sx={{ ...fieldSx, width: 160 }} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Hasta</Typography>
          <TextField type="date" value={filtros.hasta} onChange={f('hasta')}
            inputProps={{ style: { colorScheme: 'dark' } }} sx={{ ...fieldSx, width: 160 }} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Usuario</Typography>
          <TextField value={filtros.usuario} onChange={f('usuario')}
            placeholder="Buscar por nombre..." sx={{ ...fieldSx, width: 200 }} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Entidad</Typography>
          <FormControl sx={{ ...fieldSx, width: 190 }}>
            <Select value={filtros.entidad_tipo} onChange={f('entidad_tipo')}
              displayEmpty sx={{ color: '#e0e0e0' }}>
              {ENTIDADES.map(e => (
                <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography sx={labelSx}>Acción</Typography>
          <FormControl sx={{ ...fieldSx, width: 170 }}>
            <Select value={filtros.accion} onChange={f('accion')}
              displayEmpty sx={{ color: '#e0e0e0' }}>
              {ACCIONES.map(e => (
                <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: '#ef5350', mb: 2, fontSize: 14 }}>{error}</Typography>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: 'var(--c-acento)' }} />
        </Box>
      ) : (
        <>
          <Typography sx={{ fontSize: 12, color: '#546e7a', mb: 1 }}>
            {filas.length} registro{filas.length !== 1 ? 's' : ''}
            {filas.length === 500 ? ' (límite 500 — ajustá los filtros para ver más)' : ''}
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#16213e', border: '1px solid #1e3a5f' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { color: '#546e7a', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '1px solid #1e3a5f', py: 1 } }}>
                  <TableCell sx={{ width: 36 }} />
                  <TableCell>Fecha/hora</TableCell>
                  <TableCell>Entidad</TableCell>
                  <TableCell>Acción</TableCell>
                  <TableCell>Detalle</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: '#546e7a', border: 'none' }}>
                      No hay registros para los filtros seleccionados.
                    </TableCell>
                  </TableRow>
                ) : filas.map(row => (
                  <React.Fragment key={row.id}>
                    <TableRow sx={{
                      '&:hover': { bgcolor: 'rgba(144,202,249,0.04)' },
                      '& td': { borderBottom: expandido === row.id ? 'none' : '1px solid #1e3a5f', py: 1, fontSize: 13 }
                    }}>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpandido(expandido === row.id ? null : row.id)}
                          sx={{ color: '#546e7a', p: 0.3 }}>
                          {expandido === row.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ color: '#90a4ae', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {formatFechaHora(row.created_at)}
                      </TableCell>
                      <TableCell sx={{ color: '#b0bec5', whiteSpace: 'nowrap' }}>
                        {ENTIDAD_LABEL[row.entidad_tipo] || row.entidad_tipo}
                        {row.entidad_id ? ` #${row.entidad_id}` : ''}
                      </TableCell>
                      <TableCell><AccionChip valor={row.accion} /></TableCell>
                      <TableCell sx={{ color: '#90a4ae', maxWidth: 320 }}>
                        {resumenDetalle(row)}
                      </TableCell>
                      <TableCell sx={{ color: '#e0e0e0' }}>{row.usuario_nombre}</TableCell>
                      <TableCell>
                        <Chip label={row.usuario_rol} size="small"
                          sx={{ fontSize: 10, height: 18, bgcolor: 'rgba(144,202,249,0.1)', color: '#90caf9', border: '1px solid rgba(144,202,249,0.2)' }} />
                      </TableCell>
                      <TableCell sx={{ color: '#546e7a', fontSize: 11, fontFamily: 'monospace' }}>
                        {row.ip_cliente || <span style={{ color: '#37474f' }}>—</span>}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 'none' }}>
                        <Collapse in={expandido === row.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, borderBottom: '1px solid #1e3a5f' }}>
                            <DetalleJSON detalle={row.detalle} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
